import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatUSD, formatDate } from "@/lib/utils";
import {
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
  Activity,
  Clock,
  Save,
} from "lucide-react";

const TIMEOUT_PRESETS = [3, 5, 10];

function StatusDot({ online, mode }) {
  if (mode === "disabled") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-semibold uppercase tracking-widest">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Offline
      </span>
    );
  }
  const cls = online
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-red-50 text-red-700 border-red-200";
  const dot = online ? "bg-emerald-500" : "bg-red-500";
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-widest " +
        cls
      }
    >
      <span className={"w-1.5 h-1.5 rounded-full " + dot} />
      {online ? "Online" : "Offline"}
    </span>
  );
}

function StatRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5" data-testid={testid}>
      <span className="text-slate-500 text-xs">{label}</span>
      <span className="font-semibold text-slate-900 text-right">{value}</span>
    </div>
  );
}

export default function ProviderDashboardPanel() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeoutInput, setTimeoutInput] = useState("");
  const [reserveInput, setReserveInput] = useState("");
  const [savingTimeout, setSavingTimeout] = useState(false);
  const [savingReserve, setSavingReserve] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [{ data: d }, { data: s }] = await Promise.all([
        api.get("/admin/providers/dashboard"),
        api.get("/admin/providers/settings"),
      ]);
      setItems(d.items || []);
      setSettings(s);
      setTimeoutInput(String(s.provider_timeout_seconds));
      setReserveInput(String(s.min_balance_reserve_usd ?? 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // 30-second auto-refresh
    return () => clearInterval(t);
  }, []);

  const setTimeoutValue = async (v) => {
    setSavingTimeout(true);
    try {
      const { data } = await api.put("/admin/providers/settings", {
        provider_timeout_seconds: v,
      });
      setSettings(data);
      setTimeoutInput(String(data.provider_timeout_seconds));
      toast.success(`Timeout set to ${data.provider_timeout_seconds}s`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save timeout");
    } finally {
      setSavingTimeout(false);
    }
  };

  const saveCustom = async () => {
    const v = parseInt(timeoutInput, 10);
    if (!Number.isFinite(v) || v < 1 || v > 120) {
      toast.error("Timeout must be between 1 and 120 seconds.");
      return;
    }
    await setTimeoutValue(v);
  };

  const saveReserve = async () => {
    const v = parseFloat(reserveInput);
    if (!Number.isFinite(v) || v < 0 || v > 10000) {
      toast.error("Reserve must be between 0 and 10 000 USD.");
      return;
    }
    setSavingReserve(true);
    try {
      const { data } = await api.put("/admin/providers/settings", {
        min_balance_reserve_usd: v,
      });
      setSettings(data);
      setReserveInput(String(data.min_balance_reserve_usd ?? 0));
      toast.success(`Reserve buffer set to ${data.min_balance_reserve_usd.toFixed(2)} USD`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save reserve");
    } finally {
      setSavingReserve(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="provider-dashboard-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Live provider dashboard
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Auto-refreshes every 30 seconds. Balance snapshots are polled by
            the background loop every {settings?.balance_refresh_seconds || 300}s.
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
          data-testid="dashboard-refresh-btn"
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Refresh now
        </button>
      </div>

      {/* Provider cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div
            key={p.key}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
            data-testid={`dashboard-card-${p.key}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <div
                  className="text-base font-bold text-slate-900"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {p.display_name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Priority #{p.priority}
                </div>
              </div>
              <StatusDot online={p.online} mode={p.mode} />
            </div>
            <div className="border-t border-slate-100">
              <StatRow
                label="Mode"
                value={
                  <span className="uppercase tracking-widest text-[10px] font-bold">
                    {p.mode}
                  </span>
                }
              />
              <StatRow
                label="Enabled"
                value={p.enabled ? "Yes" : "No"}
                testid={`dashboard-${p.key}-enabled`}
              />
              <StatRow
                label="Balance"
                value={
                  p.balance != null
                    ? `${formatUSD(p.balance)} ${p.currency || ""}`
                    : "—"
                }
                testid={`dashboard-${p.key}-balance`}
              />
              <StatRow
                label="Avg response time"
                value={p.avg_latency_ms != null ? `${p.avg_latency_ms} ms` : "—"}
                testid={`dashboard-${p.key}-latency`}
              />
              <StatRow
                label="Last connection"
                value={
                  p.balance_checked_at
                    ? formatDate(p.balance_checked_at)
                    : "—"
                }
              />
              <StatRow
                label="Last successful topup"
                value={p.last_seen_at ? formatDate(p.last_seen_at) : "—"}
              />
            </div>
            {p.health_detail && (
              <div className="mt-2 text-[11px] text-slate-500 flex items-start gap-1">
                {p.online ? (
                  <Wifi className="w-3 h-3 mt-0.5 text-emerald-500" />
                ) : (
                  <WifiOff className="w-3 h-3 mt-0.5 text-slate-400" />
                )}
                <span>{p.health_detail}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Timeout config */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
        data-testid="provider-timeout-config"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <Clock className="w-5 h-5 text-slate-500 mt-0.5" />
          <div className="flex-1 min-w-[260px]">
            <div className="text-sm font-semibold text-slate-900">
              Provider timeout
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              If a provider does not respond within this delay, the orchestrator
              falls back to the next one — but ONLY after a probe confirms the
              upstream transaction was not accepted, guaranteeing no duplicate
              charges.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {TIMEOUT_PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => setTimeoutValue(v)}
                disabled={savingTimeout}
                data-testid={`timeout-preset-${v}`}
                className={
                  "h-8 px-3 rounded-lg text-xs font-semibold border " +
                  (settings?.provider_timeout_seconds === v
                    ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                }
              >
                {v}s
              </button>
            ))}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max="120"
                value={timeoutInput}
                onChange={(e) => setTimeoutInput(e.target.value)}
                className="w-20 h-8 px-2 rounded-lg border border-slate-300 text-sm"
                data-testid="timeout-custom-input"
              />
              <span className="text-xs text-slate-500">sec</span>
              <button
                onClick={saveCustom}
                disabled={savingTimeout}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                data-testid="timeout-custom-save"
              >
                {savingTimeout ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
          <Activity className="w-3 h-3" />
          Currently: {settings?.provider_timeout_seconds || 10} seconds. Balance
          refresh cadence: {settings?.balance_refresh_seconds || 300} seconds.
        </div>
      </div>

      {/* Balance reserve buffer */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
        data-testid="provider-reserve-config"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <Activity className="w-5 h-5 text-slate-500 mt-0.5" />
          <div className="flex-1 min-w-[260px]">
            <div className="text-sm font-semibold text-slate-900">
              Balance-aware skip
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Before each topup attempt the orchestrator checks the cached
              provider balance. If the balance minus this reserve is below
              the topup amount, the provider is skipped and the next one in
              the priority chain is used. Set to 0 to disable the reserve
              (skip still triggers when balance &lt; send amount).
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="number"
              min="0"
              max="10000"
              step="0.5"
              value={reserveInput}
              onChange={(e) => setReserveInput(e.target.value)}
              className="w-28 h-8 px-2 rounded-lg border border-slate-300 text-sm"
              data-testid="reserve-input"
            />
            <span className="text-xs text-slate-500">USD reserve</span>
            <button
              onClick={saveReserve}
              disabled={savingReserve}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
              data-testid="reserve-save"
            >
              {savingReserve ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save
            </button>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Currently: {(settings?.min_balance_reserve_usd ?? 0).toFixed(2)} USD reserve.
        </div>
      </div>
    </div>
  );
}
